import React from "react";
import { LoadPhase, SkeletonCard, skeletonRows } from "../../../components/ui";

/** As many rows as there are terms on sale. Two is what the endpoint has
 *  answered with since there has been an endpoint; a third would land as a
 *  third row and this would be one short for a moment, which is a cheaper wrong
 *  than a column of blocks for terms nobody sells. */
const PLACEHOLDER_ROWS = 2;

/** The panel on the left, at the aspect its artwork is drawn to. */
const SHOWCASE_FOOTPRINT =
  "min-h-[380px] lg:aspect-[613/444] lg:min-h-0 h-full";

/** One duration row: a term on the left, two figures on the right. */
const ROW_FOOTPRINT = "h-[76px] 3xl:h-[88px]";

export interface PlansSkeletonProps {
  /**
   * How long the price list has been coming. Only `slow` draws the shape;
   * while it is merely `waiting` the space is empty, so a list that arrives
   * quickly is never preceded by a screenful of blocks.
   */
  phase: LoadPhase;
}

/**
 * The plans screen while it is still on its way.
 *
 * The same two columns the answer lands in — the panel on the left, a stack of
 * duration rows and a button on the right — so nothing jumps when the figures
 * arrive.
 *
 * Hidden from screen readers, all of it. These are not plans, and a page that
 * announced two of them would be answering a question it does not yet know the
 * answer to; the section says "Loading" in words instead.
 */
const PlansSkeleton: React.FC<PlansSkeletonProps> = ({ phase }) => {
  if (phase !== "slow") {
    // Nothing at all while the wait is too short to notice — but the grid it
    // sits in keeps its own gaps, so what lands does not shift the page.
    return null;
  }

  return (
    <>
      <SkeletonCard className={SHOWCASE_FOOTPRINT} />
      <div className="flex flex-col gap-3 3xl:gap-4">
        {skeletonRows(PLACEHOLDER_ROWS).map((row) => (
          <SkeletonCard key={row} className={ROW_FOOTPRINT} />
        ))}
        <SkeletonCard className="h-[52px] 3xl:h-[58px]" />
      </div>
    </>
  );
};

export default PlansSkeleton;
